import React, { useState } from 'react'
import styles from './index.module.scss'
import DocumentsIcon from '../../../../assets/icon/DocumentsIcon'
import CheckIcon from '../../../../assets/icon/CheckIcon'
import DotsVerticalIcon from '../../../../assets/icon/DotsVerticalIcon'
import ArchiveIcon from '../../../../assets/icon/ArchiveIcon'
import MoveIcon from '../../../../assets/icon/MoveIcon'
import TrashIcon from '../../../../assets/icon/TrashIcon'
import DropDown from '../../../../components/DropDown'
import DocumentService from '../../../../services/document.service'
import { ProjectDocument } from '../../../../types'
import CopyLinkIcon from '../../../../assets/icon/CopyLinkIcon'
import EditIcon from '../../../../assets/icon/EditIcon'
import ActionCheckIcon from '../../../../assets/icon/ActionCheckIcon'
import CloseIcon from '../../../../assets/icon/CloseIcon'
import DocumentDuplicateIcon from '../../../../assets/icon/DocumentDuplicateIcon'
import { getProjectDocumentName, openProjectDocument } from '../../../../utils/projectDocument'
import {
    formatDocumentDate,
    getDocumentDate,
    getDocumentOwnerInitials,
    getDocumentOwnerName,
    isArchivedDocument,
} from './utils'

interface DocumentRowProps {
    document: ProjectDocument
    projectId: string
    isSelected: boolean
    isBusy: boolean
    isHighlighted: boolean
    isRenaming: boolean
    renameName: string
    onToggle: () => void
    onRenameStart: (document: ProjectDocument) => void
    onRenameChange: (value: string) => void
    onRenameConfirm: () => void
    onRenameCancel: () => void
    onCopyLink: (document: ProjectDocument) => Promise<void>
    onMoveClick: (document: ProjectDocument, rect: DOMRect) => void
    onArchiveToggle: (document: ProjectDocument) => Promise<void>
    onRefresh: () => Promise<void>
    onDeleteClick: (document: ProjectDocument) => void
}

export default function DocumentRow({
    document,
    projectId,
    isSelected,
    isBusy,
    isHighlighted,
    isRenaming,
    renameName,
    onToggle,
    onRenameStart,
    onRenameChange,
    onRenameConfirm,
    onRenameCancel,
    onCopyLink,
    onMoveClick,
    onArchiveToggle,
    onRefresh,
    onDeleteClick,
}: DocumentRowProps) {
    const [isActionLoading, setIsActionLoading] = useState(false)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const archived = isArchivedDocument(document)

    const closeMenu = () => {
        setIsMenuOpen(false)
    }

    const handleOpenDocument = async () => {
        setIsActionLoading(true)

        try {
            await openProjectDocument(projectId, document, { downloadOnFirstOpen: true })
        } catch (error) {
            console.error('Failed to open document:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    const handleDuplicate = async () => {
        closeMenu()
        setIsActionLoading(true)

        try {
            await DocumentService.duplicate(projectId, document.id)
            await onRefresh()
        } catch (error) {
            console.error('Failed to duplicate document:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    const handleCopyLink = async () => {
        closeMenu()
        setIsActionLoading(true)

        try {
            await onCopyLink(document)
        } catch (error) {
            console.error('Failed to copy document link:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    const handleArchiveToggle = async () => {
        closeMenu()
        setIsActionLoading(true)

        try {
            await onArchiveToggle(document)
        } catch (error) {
            console.error('Failed to change archive state:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    return (
        <div
            className={`${styles.documentsView__row} ${isSelected ? styles.documentsView__rowSelected : ''} ${isBusy || isActionLoading ? styles.documentsView__rowDisabled : ''} ${isHighlighted ? styles.documentsView__rowHighlighted : ''}`}
            data-document-row-id={document.id}
        >
            <div className={styles.documentsView__cellCheckbox}>
                <div
                    className={`${styles.documentsView__checkbox} ${isSelected ? styles.documentsView__checkboxActive : ''}`}
                    onClick={onToggle}
                >
                    {isSelected && <CheckIcon />}
                </div>
            </div>
            <div className={styles.documentsView__cellName}>
                {isRenaming ? (
                    <div className={styles.documentsView__renameWrap} onClick={(event) => event.stopPropagation()}>
                        <input
                            autoFocus
                            className={styles.documentsView__renameInput}
                            value={renameName}
                            onChange={(event) => onRenameChange(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') onRenameConfirm()
                                if (event.key === 'Escape') onRenameCancel()
                            }}
                        />
                        <button type="button" className={styles.documentsView__renameConfirm} onClick={onRenameConfirm}>
                            <ActionCheckIcon />
                        </button>
                        <button type="button" className={styles.documentsView__renameCancel} onClick={onRenameCancel}>
                            <CloseIcon />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className={styles.documentsView__fileButton}
                        onClick={handleOpenDocument}
                        disabled={isBusy || isActionLoading}
                    >
                        <div className={styles.documentsView__fileIcon}>
                            <DocumentsIcon />
                        </div>
                        <span className={styles.documentsView__fileName}>{getProjectDocumentName(document)}</span>
                        {archived && (
                            <span className={styles.documentsView__archivedBadge}>
                                <ArchiveIcon />
                                <span>Архивированный</span>
                            </span>
                        )}
                    </button>
                )}
            </div>
            <div className={styles.documentsView__cellOwner}>
                <div className={styles.documentsView__owner}>
                    <div className={styles.documentsView__ownerAvatar}>{getDocumentOwnerInitials(document)}</div>
                    <span className={styles.documentsView__ownerName}>{getDocumentOwnerName(document)}</span>
                </div>
            </div>
            <div className={styles.documentsView__cellDate}>
                {formatDocumentDate(getDocumentDate(document))}
            </div>
            <div className={styles.documentsView__cellActions}>
                <DropDown
                    closeDropDownByPick
                    isOpen={isMenuOpen}
                    setIsOpen={setIsMenuOpen}
                    position="right"
                    btn={(
                        <button
                            type="button"
                            className={styles.documentsView__actionsBtn}
                            disabled={isBusy || isActionLoading}
                        >
                            <DotsVerticalIcon />
                        </button>
                    )}
                >
                    <div className={styles.documentsView__dropdown}>
                        <div
                            className={styles.documentsView__dropdownItem}
                            onClick={() => {
                                closeMenu()
                                onRenameStart(document)
                            }}
                        >
                            <EditIcon />
                            <span>Переименовать</span>
                        </div>
                        <div className={styles.documentsView__dropdownItem} onClick={handleDuplicate}>
                            <DocumentDuplicateIcon />
                            <span>Дублировать</span>
                        </div>
                        <div className={styles.documentsView__dropdownItem} onClick={handleCopyLink}>
                            <CopyLinkIcon />
                            <span>Копировать ссылку</span>
                        </div>
                        <div className={styles.documentsView__dropdownItem} onClick={handleArchiveToggle}>
                            <ArchiveIcon />
                            <span>{archived ? 'Вернуть с архива' : 'Архивировать'}</span>
                        </div>
                        <div
                            className={styles.documentsView__dropdownItem}
                            onClick={(event) => {
                                closeMenu()
                                onMoveClick(document, event.currentTarget.getBoundingClientRect())
                            }}
                        >
                            <MoveIcon />
                            <span>Переместить</span>
                        </div>
                        <div
                            className={`${styles.documentsView__dropdownItem} ${styles.documentsView__dropdownItemDanger}`}
                            onClick={() => {
                                closeMenu()
                                onDeleteClick(document)
                            }}
                        >
                            <TrashIcon />
                            <span>Удалить</span>
                        </div>
                    </div>
                </DropDown>
            </div>
        </div>
    )
}
